import { getBodyTree } from "../../stores/sceneActions/bodyUtils";
import { generateUuid } from "../id";
import { getSceneState, useSceneStore } from "../../stores/sceneStore";
import { getHistoryActions } from "../../stores/historyStore";
import type { AllActions } from "../../stores/sceneActions/actionTypes";
import type { Actuator, Material, Mesh, Sensor, Site, Texture } from "../../types/scene";
import {
  remapParsedDataIdentityCollisions,
  type ParsedSceneIdentityData,
} from "../sceneIdentityRemap";
import { logger } from "../logger";
import type {
  ParsedActuatorMap,
  ParsedActuatorRecord,
  ParsedBodyMap,
  ParsedBodyTree,
  ParsedData,
  ParsedDataIdentityRemapResult,
  ParsedMeshMap,
  ParsedSceneStoreBodyMap,
  ParsedSceneStoreWorldBody,
  ParsedSensorMap,
  ParsedSensorRecord,
  ParsedTextureRecords,
  ParsedWorldBody,
  SceneLoaderOptions,
} from "./types";

/*
 * Parsed MJCF payloads contain plain string ids until scene actions normalize them
 * into branded scene UUIDs. Keep this conversion explicit at the action boundary.
 */
const asSceneBodies = (bodies: ParsedBodyMap): ParsedSceneStoreBodyMap =>
  bodies as unknown as ParsedSceneStoreBodyMap;
const asSceneWorldBodies = (worldBodies: ParsedWorldBody[]): ParsedSceneStoreWorldBody =>
  worldBodies as unknown as ParsedSceneStoreWorldBody;

export function normalizeParsedActuatorsByUuid(
  actuators: ParsedActuatorMap | ParsedActuatorRecord[] | undefined
): ParsedActuatorMap | undefined {
  if (!actuators) return actuators;
  const normalized: ParsedActuatorMap = {};
  const entries = Array.isArray(actuators)
    ? actuators.map((actuator, index) => [String(index), actuator] as const)
    : Object.entries(actuators);
  entries.forEach(([key, actuator]) => {
    if (!actuator) return;
    const uuid = actuator.uuid || generateUuid();
    normalized[String(uuid)] = actuator.name
      ? { ...actuator, uuid }
      : { ...actuator, uuid, name: key };
  });
  return normalized;
}

export function normalizeParsedSensorsByUuid(
  sensors: ParsedSensorMap | ParsedSensorRecord[] | undefined
): ParsedSensorMap | undefined {
  if (!sensors) return sensors;
  const normalized: ParsedSensorMap = {};
  const entries = Array.isArray(sensors)
    ? sensors.map((sensor, index) => [String(index), sensor] as const)
    : Object.entries(sensors);
  entries.forEach(([key, sensor]) => {
    if (!sensor) return;
    const uuid = String(sensor.uuid || generateUuid());
    normalized[String(uuid)] = sensor.name ? { ...sensor, uuid } : { ...sensor, uuid, name: key };
  });
  return normalized;
}

export function resolveParsedSensorRefsToUuid({
  sensors,
  joints,
  sites,
  bodies,
  actuators,
  tendons,
}: {
  sensors: ParsedSensorMap | undefined;
  joints: ParsedData["joints"];
  sites: ParsedData["sites"];
  bodies: ParsedData["bodies"];
  actuators: ParsedActuatorMap | undefined;
  tendons: ParsedData["tendons"];
}): ParsedSensorMap | undefined {
  if (!sensors) return sensors;

  const buildRefMap = (records: Array<{ uuid?: unknown; name?: unknown }> | undefined) => {
    const refs = new Map<string, string>();
    records?.forEach((record) => {
      if (!record?.uuid) return;
      const uuid = String(record.uuid);
      refs.set(uuid, uuid);
      if (record.name) refs.set(String(record.name), uuid);
    });
    return refs;
  };

  const jointRefs = buildRefMap(
    joints ? (Array.isArray(joints) ? joints : Object.values(joints)) : []
  );
  const siteRefs = buildRefMap(sites ? Object.values(sites) : []);
  const bodyRefs = buildRefMap(bodies ? Object.values(bodies) : []);
  const actuatorRefs = buildRefMap(actuators ? Object.values(actuators) : []);
  const tendonRefs = buildRefMap(tendons ? Object.values(tendons) : []);

  const refMaps: Record<string, Map<string, string>> = {
    joint: jointRefs,
    site: siteRefs,
    body: bodyRefs,
    tendon: tendonRefs,
    actuator: actuatorRefs,
  };

  const resolveValue = (key: string, value: unknown, objtype?: unknown): unknown => {
    if (typeof value !== "string") return value;
    const direct = refMaps[key]?.get(value);
    if (direct) return direct;
    if (key !== "objname") return value;
    const typedRefs =
      refMaps[
        String(objtype || "")
          .trim()
          .toLowerCase()
      ];
    return typedRefs?.get(value) || value;
  };

  const resolved: ParsedSensorMap = {};
  Object.entries(sensors).forEach(([key, sensor]) => {
    if (!sensor) return;
    const next: ParsedSensorRecord = { ...sensor };
    const nextRefs = next as Record<string, unknown>;
    ["joint", "site", "body", "tendon", "actuator"].forEach((refKey) => {
      if (Object.prototype.hasOwnProperty.call(nextRefs, refKey)) {
        nextRefs[refKey] = resolveValue(refKey, nextRefs[refKey]);
      }
    });
    if (next.attrs && typeof next.attrs === "object" && !Array.isArray(next.attrs)) {
      const attrs = { ...next.attrs };
      ["joint", "site", "body", "tendon", "actuator"].forEach((refKey) => {
        if (Object.prototype.hasOwnProperty.call(attrs, refKey)) {
          attrs[refKey] = resolveValue(refKey, attrs[refKey]);
        }
      });
      if (Object.prototype.hasOwnProperty.call(attrs, "objname")) {
        attrs.objname = resolveValue("objname", attrs.objname, attrs.objtype);
      }
      next.attrs = attrs;
    }
    resolved[key] = next;
  });
  return resolved;
}

/**
 * Internal function that handles the actual loading logic from parsed data.
 */
export async function loadSceneFromParsedData(
  parsedData: ParsedData,
  sceneActions: AllActions,
  options: SceneLoaderOptions = {},
  textureRecords: ParsedTextureRecords = {}
) {
  const {
    mergeWithExisting = false,
    assignCollisionTypes = false,
    collisionBitStart = 1,
    adjustPositions = false,
    folderType = "robots",
    saveToHistory = true,
    worldBodyNameBase = "Object",
  } = options;
  const identityRemap: ParsedDataIdentityRemapResult = mergeWithExisting
    ? (remapParsedDataIdentityCollisions(
        parsedData as unknown as ParsedSceneIdentityData,
        getSceneState()
      ) as unknown as ParsedDataIdentityRemapResult)
    : { parsedData, idMap: {}, remapped: false };
  const {
    materials: parsedMaterials,
    joints: parsedJoints,
    bodies: parsedBodiesMap = {},
    worldBodies: parsedWorldBodies,
    sites: parsedSitesFromParser,
    cameras: parsedCameras,
    lights: parsedLights,
    textures: parsedTextures,
    meshes: parsedMeshes,
    hfields: parsedHFields,
    actuators: parsedActuators,
    tendons: parsedTendons,
    sensors: parsedSensors,
    equalities: parsedEqualities,
    contactPairs: parsedContactPairs,
    contactExcludes: parsedContactExcludes,
    option: parsedOption,
    mjcfCompiler: parsedMjcfCompiler,
    mjcfStatistic: parsedMjcfStatistic,
  } = identityRemap.parsedData;

  const parsedBodies = getBodyTree({
    bodies: parsedBodiesMap,
    worldBodies: parsedWorldBodies || [],
  }) as unknown as ParsedBodyTree[];

  const normalizeSites = (bodies: ParsedBodyTree[], siteMap: Record<string, Site>) => {
    const sites = { ...siteMap };
    const processBody = (body: ParsedBodyTree) => {
      if (Array.isArray(body.sites)) {
        body.sites.forEach((siteUuid: string) => {
          if (siteUuid && !sites[siteUuid]) {
            logger.warn(`[sceneLoader] Site UUID ${siteUuid} not found in site map`);
          }
        });
      }
      body.children?.forEach(processBody);
    };
    bodies.forEach(processBody);
    return sites;
  };
  const parsedSites = normalizeSites(parsedBodies, parsedSitesFromParser || {});

  const generateUniqueWorldBodyName = (
    existingWorldBodies: ParsedWorldBody[] = [],
    startNumber = 1
  ) => {
    const existingNames = new Set(existingWorldBodies.map((wb) => wb.name));
    const baseLabel =
      typeof worldBodyNameBase === "string" && worldBodyNameBase.trim()
        ? worldBodyNameBase.trim()
        : "Object";
    let number = startNumber;
    let candidate = `${baseLabel} ${number}`;
    while (existingNames.has(candidate)) {
      number += 1;
      candidate = `${baseLabel} ${number}`;
    }
    return candidate;
  };
  const applyWorldBodyBaseName = (
    worldBodies: ParsedWorldBody[],
    existingWorldBodies: ParsedWorldBody[] = []
  ) => {
    if (!worldBodyNameBase) return worldBodies;
    const renamed: ParsedWorldBody[] = [];
    worldBodies.forEach((wb, index: number) => {
      const name = generateUniqueWorldBodyName([...existingWorldBodies, ...renamed], index + 1);
      renamed.push({ ...wb, name });
    });
    return renamed;
  };

  const finalBodiesMap = parsedBodiesMap;
  let finalWorldBodies: ParsedWorldBody[] = [];
  if (parsedWorldBodies && parsedWorldBodies.length > 0) {
    if (typeof parsedWorldBodies[0] === "string") {
      finalWorldBodies = parsedWorldBodies.map((entry, index: number) => {
        const uuid = typeof entry === "string" ? entry : entry.uuid;
        const name = generateUniqueWorldBodyName(finalWorldBodies, index + 1);
        return { uuid: String(uuid), name };
      });
    } else {
      finalWorldBodies = parsedWorldBodies.map((entry) => ({
        uuid: String(typeof entry === "string" ? entry : entry.uuid),
        name: typeof entry === "string" ? String(entry) : entry.name || String(entry.uuid),
      }));
    }
    if (worldBodyNameBase) {
      finalWorldBodies = applyWorldBodyBaseName(finalWorldBodies);
    }
  }
  if (assignCollisionTypes || adjustPositions) {
    const isRobot = folderType === "robots";
    const collisionBit = collisionBitStart;
    const collisionType = assignCollisionTypes ? 1 << collisionBit : undefined;
    const rootBodyUuid = finalWorldBodies.length > 0 ? finalWorldBodies[0].uuid : null;
    const rootBody = rootBodyUuid ? finalBodiesMap[rootBodyUuid] : null;
    for (const body of Object.values(finalBodiesMap)) {
      if (!body) continue;
      if (assignCollisionTypes && body.geoms) {
        body.geoms = body.geoms.map((geom) => {
          const isVisualOnly = geom.visualOnly === true;
          if (isVisualOnly) return geom;
          const hasSourceContactMask =
            geom.contactCategory !== undefined || geom.contactAffinity !== undefined;
          if (hasSourceContactMask) return geom;
          return {
            ...geom,
            contactCategory: 1 | (collisionType || 0),
            contactAffinity: 1 | (collisionType || 0),
          };
        });
      }
      if (adjustPositions && !isRobot && rootBody && body.uuid === rootBody.uuid) {
        body.position = [0, 0, 0.1];
      }
    }
  }

  let finalBodies = finalBodiesMap;
  if (mergeWithExisting) {
    const currentState = getSceneState();
    const currentBodies = currentState.bodies || {};
    finalBodies = { ...currentBodies, ...finalBodiesMap };
    const currentWorldBodies = currentState.worldBodies || [];
    if (worldBodyNameBase) {
      finalWorldBodies = applyWorldBodyBaseName(
        finalWorldBodies,
        currentWorldBodies as unknown as ParsedWorldBody[]
      );
    }
    const existingNames = new Set(currentWorldBodies.map((wb) => wb.name));
    finalWorldBodies = finalWorldBodies.map((wb, index: number) => {
      if (!wb.name || existingNames.has(wb.name)) {
        const newName = generateUniqueWorldBodyName([
          ...(currentWorldBodies as unknown as ParsedWorldBody[]),
          ...finalWorldBodies.slice(0, index),
        ]);
        existingNames.add(newName);
        return { ...wb, name: newName };
      }
      existingNames.add(wb.name);
      return wb;
    });
    finalWorldBodies = [
      ...(currentWorldBodies as unknown as ParsedWorldBody[]),
      ...finalWorldBodies,
    ];
  }

  let finalJoints = parsedJoints;
  if (mergeWithExisting && parsedJoints) {
    const currentState = getSceneState();
    const currentJoints = currentState.joints || {};
    const currentJointsArray = Object.values(currentJoints);
    const parsedJointsArray = Array.isArray(parsedJoints)
      ? parsedJoints
      : Object.values(parsedJoints);
    finalJoints = [...currentJointsArray, ...parsedJointsArray];
  }

  let finalSites = parsedSites;
  if (mergeWithExisting && parsedSites) {
    const currentState = getSceneState();
    const currentSites = currentState.sites || {};
    finalSites = { ...currentSites, ...parsedSites };
  }
  let finalCameras = parsedCameras || {};
  if (mergeWithExisting && parsedCameras) {
    const currentState = getSceneState();
    const currentCameras = currentState.cameras || {};
    finalCameras = { ...currentCameras, ...parsedCameras };
  }
  let finalLights = parsedLights || {};
  if (mergeWithExisting && parsedLights) {
    const currentState = getSceneState();
    const currentLights = currentState.lights || {};
    finalLights = { ...currentLights, ...parsedLights };
  }

  let finalTextureRecords = textureRecords || {};
  if (mergeWithExisting) {
    const currentState = getSceneState();
    const currentTextures = currentState.textures || {};
    finalTextureRecords = { ...currentTextures, ...(textureRecords || {}) };
  }

  let finalMaterials = parsedMaterials || {};
  if (mergeWithExisting) {
    const currentState = getSceneState();
    const currentMaterials = currentState.materials || {};
    finalMaterials = { ...currentMaterials, ...(parsedMaterials || {}) };
  }

  const parsedActuatorRecord = normalizeParsedActuatorsByUuid(parsedActuators);
  let finalActuators = parsedActuatorRecord;
  if (parsedActuatorRecord && parsedJoints) {
    const parsedJointsArray = Array.isArray(parsedJoints)
      ? parsedJoints
      : Object.values(parsedJoints);
    const jointNameToUuid: Record<string, string> = {};
    parsedJointsArray.forEach((joint) => {
      if (joint?.name && joint?.uuid) {
        jointNameToUuid[joint.name] = joint.uuid;
      }
    });
    const resolvedActuators: ParsedActuatorMap = {};
    Object.entries(parsedActuatorRecord).forEach(([key, actuator]) => {
      if (!actuator) return;
      const resolved = { ...actuator };
      if (resolved.joint && typeof resolved.joint === "string") {
        const isAlreadyUuid = parsedJointsArray.some((j) => j?.uuid === resolved.joint);
        if (!isAlreadyUuid) {
          const uuid = jointNameToUuid[resolved.joint];
          if (uuid) {
            resolved.joint = uuid;
          }
        }
      }
      resolvedActuators[key] = resolved;
    });
    finalActuators = resolvedActuators;
  }

  if (mergeWithExisting) {
    const currentActuators = useSceneStore.getState().actuators || {};
    finalActuators = { ...currentActuators, ...(finalActuators || {}) };
  }

  let finalTendons = parsedTendons || {};
  if (mergeWithExisting) {
    const currentTendons = useSceneStore.getState().tendons || {};
    finalTendons = { ...currentTendons, ...finalTendons };
  }

  const parsedSensorRecord = normalizeParsedSensorsByUuid(parsedSensors);
  let finalSensors =
    resolveParsedSensorRefsToUuid({
      sensors: parsedSensorRecord,
      joints: parsedJoints,
      sites: parsedSites,
      bodies: parsedBodiesMap,
      actuators: finalActuators,
      tendons: finalTendons,
    }) || {};
  if (mergeWithExisting) {
    const currentSensors = useSceneStore.getState().sensors || {};
    finalSensors = { ...currentSensors, ...finalSensors };
  }

  let finalEqualities = parsedEqualities;
  if (mergeWithExisting) {
    const currentEqualities = useSceneStore.getState().equality || {};
    const parsedEqualityArray = Array.isArray(parsedEqualities)
      ? parsedEqualities
      : Object.values(parsedEqualities || {});
    if (parsedEqualityArray.length > 0) {
      const updatedEqualities = { ...currentEqualities };
      parsedEqualityArray.forEach((eq) => {
        if (eq && eq.uuid) {
          updatedEqualities[eq.uuid] = eq;
        }
      });
      finalEqualities = Object.values(updatedEqualities);
    } else {
      finalEqualities = currentEqualities;
    }
  }

  let normalizedMeshes: ParsedMeshMap | undefined = undefined;
  const nameToUuidMap: Record<string, string> = {};
  if (parsedMeshes && Object.keys(parsedMeshes).length > 0) {
    normalizedMeshes = {};
    Object.entries(parsedMeshes).forEach(([inputKey, meshDef]) => {
      if (!meshDef) return;
      const existingUuid = meshDef.uuid;
      const finalUuid = existingUuid || generateUuid();
      nameToUuidMap[inputKey] = finalUuid;
      normalizedMeshes![finalUuid] = {
        ...meshDef,
        uuid: finalUuid,
        name: meshDef.name || inputKey,
      };
    });
  }

  const remapBodiesMeshRefsToUuid = (bodiesMap: ParsedBodyMap) => {
    if (!bodiesMap || Object.keys(bodiesMap).length === 0) return bodiesMap;
    const next: ParsedBodyMap = {};
    Object.entries(bodiesMap).forEach(([bodyId, body]) => {
      if (!body) return;
      if (!Array.isArray(body.geoms) || body.geoms.length === 0) {
        next[bodyId] = body;
        return;
      }
      let changed = false;
      const updatedGeoms = body.geoms.map((geom) => {
        if (!geom || typeof geom.mesh !== "string") return geom;
        const key = geom.mesh;
        const mapped = nameToUuidMap[key];
        if (!mapped) return geom;
        changed = true;
        return { ...geom, mesh: mapped };
      });
      next[bodyId] = changed ? { ...body, geoms: updatedGeoms } : body;
    });
    return next;
  };

  const ensureGeomUuids = (bodiesMap: ParsedBodyMap) => {
    if (!bodiesMap || Object.keys(bodiesMap).length === 0) return bodiesMap;
    const used = new Set<string>();
    const next: ParsedBodyMap = {};
    Object.entries(bodiesMap).forEach(([bodyId, body]) => {
      if (!body || !Array.isArray(body.geoms) || body.geoms.length === 0) {
        next[bodyId] = body;
        return;
      }
      let changed = false;
      const geoms = body.geoms.map((geom) => {
        if (!geom) return geom;
        let uuid = geom.uuid;
        const uuidStr = uuid ? String(uuid) : "";
        if (!uuidStr || used.has(uuidStr)) {
          uuid = generateUuid();
          changed = true;
        }
        used.add(String(uuid));
        return uuid === geom.uuid ? geom : { ...geom, uuid };
      });
      next[bodyId] = changed ? { ...body, geoms } : body;
    });
    return next;
  };

  const remapBodiesMaterialRefsToUuid = (
    bodiesMap: ParsedBodyMap,
    materialKeyMap: Record<string, string>
  ) => {
    if (!bodiesMap || Object.keys(bodiesMap).length === 0) return bodiesMap;
    if (!materialKeyMap || Object.keys(materialKeyMap).length === 0) return bodiesMap;
    const next: ParsedBodyMap = {};
    Object.entries(bodiesMap).forEach(([bodyId, body]) => {
      if (!body || !Array.isArray(body.geoms) || body.geoms.length === 0) {
        next[bodyId] = body;
        return;
      }
      let changed = false;
      const geoms = body.geoms.map((geom) => {
        if (!geom || !Array.isArray(geom.materials)) return geom;
        const materials = geom.materials.map((materialRef) => {
          const key = typeof materialRef === "string" ? materialRef : "";
          const mapped = key ? materialKeyMap[key] : undefined;
          if (mapped && mapped !== materialRef) changed = true;
          return mapped || materialRef;
        });
        return changed ? { ...geom, materials } : geom;
      });
      next[bodyId] = changed ? { ...body, geoms } : body;
    });
    return next;
  };

  const remappedBodies = normalizedMeshes ? remapBodiesMeshRefsToUuid(finalBodies) : finalBodies;
  const normalizedBodies = ensureGeomUuids(remappedBodies);

  const beforeSnapshot = saveToHistory ? getSceneState() : null;

  const materialKeyMap = sceneActions.loadMaterials(finalMaterials as Record<string, Material>);
  if (!mergeWithExisting && parsedOption) {
    sceneActions.updateOption(parsedOption);
  }
  if (!mergeWithExisting && parsedMjcfCompiler) {
    sceneActions.updateMjcfCompiler(parsedMjcfCompiler);
  }
  if (!mergeWithExisting && parsedMjcfStatistic) {
    sceneActions.updateMjcfStatistic(parsedMjcfStatistic);
  }
  sceneActions.setTextures(finalTextureRecords as Record<string, Texture>);
  if (normalizedMeshes) {
    sceneActions.upsertMeshes(normalizedMeshes as unknown as Record<string, Partial<Mesh>>);
  } else if (parsedMeshes) {
    sceneActions.upsertMeshes(parsedMeshes as unknown as Record<string, Partial<Mesh>>);
  }
  if (parsedHFields) {
    const currentHFields = mergeWithExisting ? getSceneState().hfields || {} : {};
    sceneActions.setHFields({ ...currentHFields, ...parsedHFields });
  }
  const materialRemappedBodies = remapBodiesMaterialRefsToUuid(
    normalizedBodies,
    materialKeyMap as Record<string, string>
  );
  sceneActions.setBodies(
    asSceneBodies(materialRemappedBodies),
    asSceneWorldBodies(finalWorldBodies)
  );
  if (finalJoints) sceneActions.setJoints(finalJoints);
  if (finalSites) sceneActions.setSites(finalSites);
  if (finalCameras) sceneActions.setCameras(finalCameras);
  if (finalLights) sceneActions.setLights(finalLights);
  if (finalActuators)
    sceneActions.setActuators(finalActuators as unknown as Record<string, Partial<Actuator>>);
  if (finalTendons) sceneActions.setTendons(finalTendons);
  sceneActions.setSensors(finalSensors as unknown as Record<string, Sensor | Partial<Sensor>>);
  if (finalEqualities) sceneActions.setEqualities(finalEqualities);

  let finalContactPairs = parsedContactPairs || [];
  if (mergeWithExisting) {
    const currentPairs = useSceneStore.getState().contactPairs || [];
    finalContactPairs = [...currentPairs, ...finalContactPairs];
  }
  if (finalContactPairs.length > 0) sceneActions.setContactPairs(finalContactPairs);

  let finalContactExcludes = parsedContactExcludes || [];
  if (mergeWithExisting) {
    const currentExcludes = useSceneStore.getState().contactExcludes || [];
    finalContactExcludes = [...currentExcludes, ...finalContactExcludes];
  }
  if (finalContactExcludes.length > 0) sceneActions.setContactExcludes(finalContactExcludes);

  if (saveToHistory && beforeSnapshot) {
    try {
      const historyActions = getHistoryActions();
      historyActions.save(beforeSnapshot);
    } catch (error) {
      logger.warn("[sceneLoader] Skipping history save due to error:", error);
    }
  }

  return {
    parsedResult: {
      materials: parsedMaterials,
      joints: finalJoints || [],
      bodies: finalBodies,
      worldBodies: finalWorldBodies,
      sites: parsedSites,
      cameras: finalCameras,
      lights: finalLights,
      meshes: parsedMeshes,
      hfields: parsedHFields,
      actuators: finalActuators,
      tendons: finalTendons,
      sensors: finalSensors,
      equalities: finalEqualities,
      contactPairs: finalContactPairs,
      textures: parsedTextures,
      option: parsedOption,
      mjcfCompiler: parsedMjcfCompiler,
      mjcfStatistic: parsedMjcfStatistic,
      importedJoints: parsedJoints,
      identityRemap: identityRemap.remapped ? identityRemap.idMap : undefined,
    },
    textureRecords,
  };
}
